/**
 * Impact Receipt — post-Stripe thank-you card.
 *
 * Feb 22 2026 · Michael's Impact Receipt ask.
 *
 * A share-friendly overlay that appears once Stripe confirms a
 * donation. Every number is real (backed by `/api/oml/impact/{sid}`):
 *
 *   • Donor's own gift — N lights, currency, country
 *   • Global running total (matches the arrival hero counter)
 *   • Today's lights across the world (last 24 h)
 *   • Top countries the movement reached today
 *
 * The design is intentionally screenshottable — one clean card the
 * donor can capture and pass on. Native share is preferred when
 * available; we fall back to a clipboard copy with a soft toast.
 */
import React, { useMemo, useRef, useState } from "react";

const TOK = {
  ink: "#050b1a",
  navy: "#131f38",
  cream: "#f5ecd9",
  creamDim: "rgba(245,236,217,0.78)",
  creamMist: "rgba(245,236,217,0.44)",
  gold: "#e0a97a",
  goldWarm: "#f0c790",
  goldSoft: "rgba(224,169,122,0.86)",
  goldMist: "rgba(224,169,122,0.24)",
};

const SYMBOL = { gbp: "£", usd: "$", eur: "€" };

// A small ISO-country-code → flag map. Kept short — anything outside
// the map falls back to the country code text so we never render an
// empty pin. Feb 22 2026 · Michael.
const FLAG = {
  GB: "🇬🇧", US: "🇺🇸", DE: "🇩🇪", FR: "🇫🇷", ES: "🇪🇸", IT: "🇮🇹",
  IE: "🇮🇪", NL: "🇳🇱", CA: "🇨🇦", AU: "🇦🇺", NZ: "🇳🇿", SE: "🇸🇪",
  NO: "🇳🇴", DK: "🇩🇰", FI: "🇫🇮", CH: "🇨🇭", AT: "🇦🇹", PT: "🇵🇹",
  BE: "🇧🇪", PL: "🇵🇱", ZA: "🇿🇦", IN: "🇮🇳", JP: "🇯🇵", SG: "🇸🇬",
  BR: "🇧🇷", MX: "🇲🇽", AR: "🇦🇷", CL: "🇨🇱",
};

function countryLabel(cc) {
  const up = String(cc || "").toUpperCase();
  return FLAG[up] || up;
}

function fmtNumber(n) {
  return Number(n || 0).toLocaleString();
}

export default function ImpactReceipt({ receipt, onClose }) {
  const [copied, setCopied] = useState(false);
  const cardRef = useRef(null);
  const sym = SYMBOL[(receipt.currency || "gbp").toLowerCase()] || "£";
  const lights = Number(receipt.lights || 1);
  const amount = Number(receipt.amount || 1);
  const runningTotal = Number(receipt.running_total || 0);
  const todayCount = Number(receipt.today_count || 0);
  const todayCountries = Array.isArray(receipt.today_countries) ? receipt.today_countries : [];

  const shareText = useMemo(() => {
    if (lights === 1) {
      return `I just placed a light on One Million Lights — a global thank-you to the carers quietly holding the world together. ${sym}${amount} · Foundations for 1 person. Join me at`;
    }
    return `I just placed ${lights} lights on One Million Lights — a global thank-you to the carers quietly holding the world together. ${sym}${amount} · Foundations for ${lights} people. Join me at`;
  }, [lights, amount, sym]);

  const shareUrl = typeof window !== "undefined"
    ? `${window.location.origin}/?brand=oml`
    : "https://onemillionlights.today";

  const onShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: "One Million Lights",
          text: shareText,
          url: shareUrl,
        });
        return;
      }
    } catch (_) { /* user cancelled — fall through to clipboard */ }
    try {
      await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (_) { /* no clipboard access — no-op */ }
  };

  const onWhatsApp = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const onX = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div
      className="fixed inset-0 z-[70] overflow-y-auto"
      style={{
        background: "rgba(6,14,31,0.94)",
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
      }}
      data-testid="oml-impact-receipt"
      onClick={onClose}
    >
      <div className="min-h-full flex items-start justify-center py-10 px-5">
        <div
          ref={cardRef}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-md rounded-3xl overflow-hidden relative"
          style={{
            background: "linear-gradient(160deg, #1a2a4a 0%, #0d1f3a 60%, #060e1f 100%)",
            border: `1px solid ${TOK.goldMist}`,
            boxShadow: `0 30px 90px rgba(0,0,0,0.6), 0 0 60px rgba(224,169,122,0.16)`,
          }}
        >
          {/* Warm gold halo top */}
          <div
            aria-hidden
            className="absolute inset-x-0 top-0 h-40 pointer-events-none"
            style={{
              background: "radial-gradient(ellipse at 50% 0%, rgba(240,199,144,0.42) 0%, rgba(224,169,122,0.15) 35%, transparent 65%)",
            }}
          />

          <div className="relative px-6 pt-8 pb-6 text-center">
            <div
              className="uppercase"
              style={{
                color: TOK.goldSoft,
                fontFamily: '"Manrope", sans-serif',
                fontSize: "0.6rem",
                letterSpacing: "0.4em",
              }}
            >
              ✦  Impact Receipt  ✦
            </div>
            <div
              className="mt-6 italic"
              style={{
                color: TOK.cream,
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: "clamp(1.55rem, 5.5vw, 2rem)",
                fontWeight: 300,
                lineHeight: 1.2,
              }}
              data-testid="oml-impact-headline"
            >
              Thank you.
              <br />
              <span style={{ color: TOK.gold }}>
                Your light is placed.
              </span>
            </div>
            <p
              className="mt-5 italic max-w-sm mx-auto"
              style={{
                color: TOK.creamDim,
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: "clamp(0.98rem, 3.2vw, 1.08rem)",
                lineHeight: 1.55,
              }}
            >
              {lights === 1
                ? `${sym}${amount} · Foundations for 1 person.`
                : `${sym}${amount} · Foundations for ${lights} people.`}
              <br />
              <span style={{ color: TOK.creamMist, fontSize: "0.9rem" }}>
                Placed from {countryLabel(receipt.country)}
              </span>
            </p>
          </div>

          {/* Movement counters */}
          <div className="px-6 pb-6">
            <div
              className="rounded-2xl p-5"
              style={{
                background: "rgba(245,236,217,0.045)",
                border: `1px solid ${TOK.goldMist}`,
              }}
            >
              <div
                className="uppercase text-center"
                style={{
                  color: TOK.goldSoft,
                  fontFamily: '"Manrope", sans-serif',
                  fontSize: "0.55rem",
                  letterSpacing: "0.32em",
                }}
              >
                One Million Lights · Live
              </div>
              <div className="mt-3 flex items-baseline justify-center gap-2">
                <span
                  style={{
                    color: TOK.gold,
                    fontFamily: '"Cormorant Garamond", serif',
                    fontSize: "2.6rem",
                    fontWeight: 500,
                    lineHeight: 1,
                  }}
                  data-testid="oml-impact-running-total"
                >
                  {fmtNumber(runningTotal)}
                </span>
                <span
                  style={{
                    color: TOK.creamMist,
                    fontFamily: '"Manrope", sans-serif',
                    fontSize: "0.7rem",
                    letterSpacing: "0.22em",
                  }}
                >
                  / 1,000,000
                </span>
              </div>
              <div
                className="text-center mt-1 uppercase"
                style={{
                  color: TOK.creamMist,
                  fontFamily: '"Manrope", sans-serif',
                  fontSize: "0.56rem",
                  letterSpacing: "0.3em",
                }}
              >
                lights placed
              </div>

              <div className="mt-5 grid grid-cols-2 gap-3">
                <div
                  className="rounded-xl px-3 py-3 text-center"
                  style={{
                    background: "rgba(19,31,56,0.55)",
                    border: `1px solid rgba(245,236,217,0.10)`,
                  }}
                  data-testid="oml-impact-today-count"
                >
                  <div
                    style={{
                      color: TOK.gold,
                      fontFamily: '"Cormorant Garamond", serif',
                      fontSize: "1.55rem",
                      fontWeight: 500,
                      lineHeight: 1,
                    }}
                  >
                    {fmtNumber(todayCount)}
                  </div>
                  <div
                    className="mt-1 uppercase"
                    style={{
                      color: TOK.creamMist,
                      fontFamily: '"Manrope", sans-serif',
                      fontSize: "0.5rem",
                      letterSpacing: "0.28em",
                    }}
                  >
                    today
                  </div>
                </div>
                <div
                  className="rounded-xl px-3 py-3 text-center"
                  style={{
                    background: "rgba(19,31,56,0.55)",
                    border: `1px solid rgba(245,236,217,0.10)`,
                  }}
                >
                  <div
                    style={{
                      color: TOK.gold,
                      fontFamily: '"Cormorant Garamond", serif',
                      fontSize: "1.55rem",
                      fontWeight: 500,
                      lineHeight: 1,
                    }}
                    data-testid="oml-impact-country-count"
                  >
                    {todayCountries.length}
                  </div>
                  <div
                    className="mt-1 uppercase"
                    style={{
                      color: TOK.creamMist,
                      fontFamily: '"Manrope", sans-serif',
                      fontSize: "0.5rem",
                      letterSpacing: "0.28em",
                    }}
                  >
                    countries today
                  </div>
                </div>
              </div>

              {todayCountries.length > 0 && (
                <div
                  className="mt-4 flex flex-wrap justify-center gap-1.5"
                  data-testid="oml-impact-country-list"
                >
                  {todayCountries.map((c) => (
                    <div
                      key={c.cc}
                      className="rounded-full px-3 py-1 flex items-center gap-1.5"
                      style={{
                        background: "rgba(224,169,122,0.10)",
                        border: `1px solid ${TOK.goldMist}`,
                        fontFamily: '"Manrope", sans-serif',
                        fontSize: "0.6rem",
                        color: TOK.creamDim,
                        letterSpacing: "0.16em",
                      }}
                    >
                      <span style={{ fontSize: "0.95rem", lineHeight: 1 }}>
                        {countryLabel(c.cc)}
                      </span>
                      <span>· {c.n}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Share affordances. Native share when available;
                WhatsApp + X as explicit wayfinders. */}
            <div className="mt-6 space-y-2.5">
              <button
                type="button"
                onClick={onShare}
                data-testid="oml-impact-share"
                className="w-full rounded-full flex items-center justify-center gap-2"
                style={{
                  background: "linear-gradient(180deg, #f0c790 0%, #d99b6b 100%)",
                  border: "1px solid #f5e0c5",
                  color: "#1a0f05",
                  fontFamily: '"Manrope", sans-serif',
                  fontSize: "0.78rem",
                  fontWeight: 700,
                  letterSpacing: "0.22em",
                  textTransform: "uppercase",
                  padding: "16px 18px",
                  boxShadow: "0 12px 44px rgba(224,169,122,0.28)",
                }}
              >
                <span aria-hidden style={{ fontSize: "1rem", lineHeight: 1 }}>↗</span>
                {copied ? "Copied to clipboard" : "Share your light"}
              </button>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={onWhatsApp}
                  data-testid="oml-impact-share-whatsapp"
                  className="rounded-full uppercase"
                  style={{
                    background: "rgba(37,211,102,0.14)",
                    border: "1px solid rgba(37,211,102,0.55)",
                    color: "#a3ecc2",
                    fontFamily: '"Manrope", sans-serif',
                    fontSize: "0.66rem",
                    letterSpacing: "0.22em",
                    padding: "12px 14px",
                  }}
                >
                  WhatsApp
                </button>
                <button
                  type="button"
                  onClick={onX}
                  data-testid="oml-impact-share-x"
                  className="rounded-full uppercase"
                  style={{
                    background: "rgba(245,236,217,0.06)",
                    border: `1px solid ${TOK.goldMist}`,
                    color: TOK.cream,
                    fontFamily: '"Manrope", sans-serif',
                    fontSize: "0.66rem",
                    letterSpacing: "0.22em",
                    padding: "12px 14px",
                  }}
                >
                  Share on X
                </button>
              </div>
            </div>

            <button
              type="button"
              onClick={onClose}
              data-testid="oml-impact-close"
              className="mt-6 w-full rounded-full uppercase"
              style={{
                background: "transparent",
                color: TOK.creamMist,
                fontFamily: '"Manrope", sans-serif',
                fontSize: "0.6rem",
                letterSpacing: "0.28em",
                padding: "10px 8px",
                borderTop: `1px dashed ${TOK.goldMist}`,
              }}
            >
              See my light on the globe →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
